import React, { useMemo } from "react";
import { Drawer } from "vaul";
import { Loader2, TrendingUp, TrendingDown, Brain } from "lucide-react";
import { PriceAlertEventData } from "@/api/price-alerts";
import { usePriceAlertSymbolHistory } from "@/hooks/usePriceAlerts";
import { useTokenMetadata } from "@/hooks/useTokenMetadata";
import { useTokenAvailability } from "@/hooks/useMarketData";
import { useUserExchanges } from "@/hooks/useExchanges";
import {
  TradingViewChartWidget,
} from "@crypto-trading/ui-shared/client";
import { PriceAlertHistoryList } from "@crypto-trading/ui-shared";
import { TokenAnalysisSection } from "@/components/TokenAnalysisSection";
import { TokenIcon } from "@/components/TokenIcon";
import { ensureUsdtSymbol, extractBaseCurrency } from "@/utils/symbols";

interface PriceAlertAnalyzeDrawerProps {
  userId: string;
  event: PriceAlertEventData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTrade?: (symbol: string) => void;
}

const formatPrice = (price: number) =>
  `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;

const formatDateTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h24",
  });
};

export const PriceAlertAnalyzeDrawer: React.FC<PriceAlertAnalyzeDrawerProps> = ({
  userId,
  event,
  open,
  onOpenChange,
  onTrade,
}) => {
  const symbol = event?.symbol ?? "";
  const normalizedSymbol = useMemo(() => (symbol ? ensureUsdtSymbol(symbol) : ""), [symbol]);
  const baseCurrency = useMemo(() => (symbol ? extractBaseCurrency(symbol) : ""), [symbol]);

  const { data: tokenMetadata, isLoading: isLoadingMetadata } = useTokenMetadata(baseCurrency, open);
  const { data: symbolHistory = [], isLoading: isLoadingHistory } = usePriceAlertSymbolHistory(symbol, open);
  const { data: exchanges = [] } = useUserExchanges(userId, true);
  const { data: tokenAvailability, isLoading: isLoadingAvailability } = useTokenAvailability(userId, normalizedSymbol, open);

  const globallyAvailableExchangeCodes = useMemo(
    () => tokenAvailability?.userExchanges ?? [],
    [tokenAvailability?.userExchanges],
  );
  const globallyAvailableSet = useMemo(() => new Set(globallyAvailableExchangeCodes), [globallyAvailableExchangeCodes]);
  const supportedUserExchangeCodes = useMemo(
    () => exchanges.filter((e) => !e.code.endsWith("-demo") && !e.code.endsWith("-testnet")).map((e) => e.code).filter((code) => globallyAvailableSet.has(code)),
    [exchanges, globallyAvailableSet],
  );
  const hasTradableExchange = supportedUserExchangeCodes.length > 0;

  if (!event || !open) return null;

  const isSpike = event.alertType.toLowerCase() === "spike";

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[100]" />
        <Drawer.Content className="bg-[#1a1b20] flex rounded-t-xl flex-col fixed bottom-0 left-0 right-0 max-h-[90vh] z-[101]">
          {/* Header */}
          <div className="mx-4 border-b border-gray-500/30 pb-3 pt-2 sticky top-0 bg-secondary z-10">
            <Drawer.Handle className="h-1 w-1/3 mt-0 mb-3 rounded-full" />
            <h2 className="text-xs text-text-primary text-center">Price Alert Analysis</h2>
            <div className="flex items-center gap-2 mt-3">
              {isSpike ? (
                <TrendingUp className="w-5 h-5 text-green-400" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-400" />
              )}
              <h2 className="text-md font-bold text-text-primary">{baseCurrency}</h2>
              <TokenIcon symbol={baseCurrency} logo={tokenMetadata?.logo} size="sm" />
              <span
                className={`px-2 py-0.5 rounded-full text-xs ${
                  isSpike ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                }`}
              >
                {isSpike ? "Spike" : "Drop"}
              </span>
              <span className="text-xs text-accent rounded-full px-2 py-0.5 bg-accent/10 capitalize">
                {event.exchange?.replace("_", " ")}
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto space-y-4">
            <div className="p-4 space-y-6">
              {/* Alert Info Grid */}
              <div className="space-y-4 p-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-text-secondary mb-1">Change</p>
                    <p className={`text-sm font-medium ${event.changePercent >= 0 ? "text-green-400" : "text-red-400"}`}>
                      {event.changePercent >= 0 ? "+" : ""}{event.changePercent.toFixed(2)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-text-secondary mb-1">Timeframe</p>
                    <p className="text-sm text-text-primary">{event.interval}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-text-secondary mb-1">Start Price</p>
                    <p className="text-sm text-text-primary">{formatPrice(event.startPrice)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-secondary mb-1">Current Price</p>
                    <p className="text-sm text-text-primary">{formatPrice(event.currentPrice)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-1">Alert Time</p>
                  <p className="text-sm text-text-primary">{formatDateTime(event.timestamp)}</p>
                </div>
              </div>

              {/* Token Metadata */}
              {isLoadingMetadata && (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-accent" />
                </div>
              )}
              {tokenMetadata?.description && (
                <div className="space-y-3 bg-primary/40 border border-gray-500/30 rounded-lg p-3">
                  <h3 className="text-sm font-bold text-text-primary">Token Information</h3>
                  <p className="text-sm text-text-primary leading-relaxed line-clamp-4">
                    {tokenMetadata.description}
                  </p>
                  {tokenMetadata.urls && (
                    <div className="flex flex-wrap gap-2">
                      {tokenMetadata.urls.website?.[0] && (
                        <a href={tokenMetadata.urls.website[0]} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors">Website</a>
                      )}
                      {tokenMetadata.urls.twitter?.[0] && (
                        <a href={tokenMetadata.urls.twitter[0]} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors">Twitter</a>
                      )}
                    </div>
                  )}
                </div>
              )}

              {(isLoadingHistory || symbolHistory.length > 0) && (
                <PriceAlertHistoryList
                  variant="drawer"
                  title={`Alert History (${symbolHistory.length})`}
                  events={symbolHistory.map((h) => ({
                    id: h.id,
                    alertType: h.alertType,
                    exchange: h.exchange,
                    changePercent: h.changePercent,
                    interval: h.interval,
                    timestamp: h.timestamp,
                    currentPrice: h.currentPrice ?? null,
                  }))}
                  isLoading={isLoadingHistory}
                />
              )}

              {/* Exchange Availability */}
              {!isLoadingAvailability && globallyAvailableExchangeCodes.length > 0 && (
                <div className="space-y-3 bg-primary/40 border border-gray-500/30 rounded-lg p-3">
                  <h3 className="text-sm font-bold text-text-primary">Exchange Availability</h3>
                  <div className="flex flex-wrap gap-2">
                    {globallyAvailableExchangeCodes.slice(0, 6).map((code) => (
                      <span
                        key={code}
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          supportedUserExchangeCodes.includes(code)
                            ? "bg-green-500/20 text-green-400"
                            : "bg-gray-500/20 text-gray-400"
                        }`}
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Price Chart */}
              <div>
                <h3 className="text-sm font-bold text-text-primary mb-3">Price Chart</h3>
                <TradingViewChartWidget key={`chart-${normalizedSymbol}`} symbol={normalizedSymbol} />
              </div>


              {/* AI Analysis */}
              <div>
                <h3 className="text-sm font-bold text-text-primary flex items-center gap-2 mb-3">
                  <Brain className="w-4 h-4 text-accent" />
                  AI Analysis
                </h3>
                <TokenAnalysisSection tokenSymbol={baseCurrency} />
              </div>
            </div>
          </div>

          {/* Footer */}
          {hasTradableExchange && onTrade && (
            <div className="sticky bottom-0 bg-[#1a1b20] border-t border-gray-500/30 p-4">
              <button
                type="button"
                onClick={() => {
                  onTrade(normalizedSymbol);
                  onOpenChange(false);
                }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white font-semibold bg-green-600 hover:bg-green-700 transition-colors"
              >
                Trade {baseCurrency}
              </button>
            </div>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
